import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';

import { useMediaData } from '@/app/library/composables/useMediaData';
import {
  POST_VOLUME_GRAPH_OPTIONS,
  SENTIMENT_TREND_CHART_OPTIONS,
  SOCIAL_LISTENING_CHANNEL_OPTIONS,
  SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS,
  SOCIAL_LISTENING_TOP_POST_SENTIMENT_CHIP_PROPS,
  TOPICS_QUERY_PARAM_MAP,
} from '@/app/socialListening/constants';
import { groupByPrimaryPercentBreakdown } from '@/app/socialListening/utils/graph-utils';
import { getSentimentKeys } from '@/app/socialListening/utils/metric-utils';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { useAuthStore } from '@/stores/auth';
import { formatVideoDurationInSeconds } from '@/utils/formatters';
import { getUserTimezone } from '@/utils/timezone';

export const mentionTypeMap = {
  AUDIO: 'Audio',
  BLOGS: 'Blog',
  CAPTION: 'Caption',
  COMMENT: 'Comment',
  DISCUSSIONS: 'Forum',
  NEWS: 'News',
  UGC: 'UGC',
};

export function formatCustomSelectDropdownLabel(start, end, format) {
  const startLabel = dayjs(start).format(format);
  const endLabel = dayjs(end).format(format);
  return `${startLabel} - ${endLabel}`;
}

export function formatTopicQueryParamsFromSourceLink(query) {
  const authStore = useAuthStore();
  const tempFilters = {
    visualData: {
      includes: [],
      doesNotInclude: [],
    },
  };
  const sentiment = {};

  // format the values from query param to be used in the filters pinia store object
  [
    TOPICS_QUERY_PARAM_MAP.SOURCES,
    TOPICS_QUERY_PARAM_MAP.SOURCE_AND_CREATOR_IDS,
    TOPICS_QUERY_PARAM_MAP.INDUSTRY_IDS,
  ].forEach((key) => {
    if (query[key]) {
      tempFilters[key] = query[key];
    }
  });

  [TOPICS_QUERY_PARAM_MAP.MEDIA_TYPES, TOPICS_QUERY_PARAM_MAP.MENTION_TYPES].forEach((key) => {
    if (query[key]) {
      tempFilters[key] = { includes: query[key] };
    }
  });

  [
    TOPICS_QUERY_PARAM_MAP.POSITIVE_SENTIMENT,
    TOPICS_QUERY_PARAM_MAP.NEUTRAL_SENTIMENT,
    TOPICS_QUERY_PARAM_MAP.NEGATIVE_SENTIMENT,
  ].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(query, key)) {
      sentiment[key] = query[key] === true || query[key] === 'true';
    }
  });
  [TOPICS_QUERY_PARAM_MAP.VISUAL_DATA_INCLUDE, TOPICS_QUERY_PARAM_MAP.VISUAL_DATA_EXCLUDE].forEach(
    (key) => {
      if (query[key]) {
        if (key.includes('include') || key.includes('exclude')) {
          const visualData = query[key];
          const targetArray = key.includes('include')
            ? tempFilters.visualData.includes
            : tempFilters.visualData.doesNotInclude;

          visualData.forEach((e) => {
            const keyName = e.split(':')[0].includes(authStore.currentBrand?.id.toString())
              ? 'libraryMediaDetails'
              : 'sourceAndId';
            targetArray.push({ [keyName]: e });
          });
        }
      }
    },
  );

  if (
    !tempFilters.visualData?.includes?.length &&
    !tempFilters.visualData?.doesNotInclude?.length
  ) {
    delete tempFilters.visualData;
  }

  if (Object.keys(sentiment).length > 0) {
    Object.keys(sentiment).forEach((key) => {
      if (sentiment[key] === true) {
        tempFilters.sentiment = sentiment;
      }
    });
  }
  return tempFilters;
}

export function formatTopicQueryParamsVisualDataForSourceLink(queryParams) {
  const formattedVisualDataQueryParams = {
    includeVisualDataSourceAndId: [],
    excludeVisualDataSourceAndId: [],
  };

  [TOPICS_QUERY_PARAM_MAP.VISUAL_DATA_INCLUDE, TOPICS_QUERY_PARAM_MAP.VISUAL_DATA_EXCLUDE].forEach(
    (key) => {
      if (queryParams[key]) {
        if (key.includes('include') || key.includes('exclude')) {
          const sourceAndIds = queryParams[key];
          const targetArray = key.includes('include')
            ? formattedVisualDataQueryParams.includeVisualDataSourceAndId
            : formattedVisualDataQueryParams.excludeVisualDataSourceAndId;

          sourceAndIds.forEach((e) => {
            const keyName = Object.keys(e)[0];
            targetArray.push(e[keyName]);
          });
        }
      }
    },
  );
  return formattedVisualDataQueryParams;
}

export function getFormattedSentimentChartData(data, filters) {
  const datasets = [];
  const breakdownGroups = groupByPrimaryPercentBreakdown(
    data,
    POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_SENTIMENT.value,
    filters.sentiments.map((key) => key.toLowerCase()),
  );
  Object.entries(breakdownGroups).forEach(([label, dataList]) => {
    datasets.push({
      label,
      data: dataList.map((d) => ({ ...d, y: d.totalPosts.replace(',', '') })),
      borderColor: SENTIMENT_TREND_CHART_OPTIONS.sentimentColors[label.toUpperCase()],
    });
  });
  return sortBy(
    datasets,
    (set) => SENTIMENT_TREND_CHART_OPTIONS.legendSort[set.label.toUpperCase()],
  );
}

/**
 * Formats the sentiment chip props for the media card.
 * @param {Object} sentiment - The sentiment object attached to the returned top post
 * @param {boolean} sentiment.isNegative - Whether the sentiment is negative
 * @param {boolean} sentiment.isNeutral - Whether the sentiment is neutral
 * @param {boolean} sentiment.isPositive - Whether the sentiment is positive
 * @param {boolean} sentiment.isQuestion - Whether the sentiment is a question
 *
 * @returns {{
 *  label: 'Positive',
 *  size: 'sm',
 *  badge: { severity: 'positive' },
 *}}
 */
export function formatSentimentChipProps(sentiment) {
  const mediaCardSentimentMetrics = {};
  if (sentiment) {
    // Only use sentiment keys and filter out isQuestion key
    const sentimentMetricKeys = getSentimentKeys(sentiment);
    sentimentMetricKeys.reduce((acc, sentimentKey) => {
      const sentimentMetric = SOCIAL_LISTENING_TOP_POST_SENTIMENT_CHIP_PROPS[sentimentKey];
      if (sentiment[sentimentKey]) {
        acc.label = sentimentMetric.label;
        acc.size = sentimentMetric.size;
        acc.badge = { severity: sentimentMetric.badge.severity };
      }
      return acc;
    }, mediaCardSentimentMetrics);
  }
  return mediaCardSentimentMetrics;
}

export function formatTopPostPostDate(postDate, formatYear = false) {
  const timezone = getUserTimezone();
  const formattedPostDate = dayjs.utc(postDate).tz(timezone);
  if (formatYear) {
    const isCurrYear = formattedPostDate.year() === dayjs().tz(timezone).year();
    return formattedPostDate.format(`MMM D,${isCurrYear ? '' : ' YYYY'} h:mm a`);
  }
  return formattedPostDate.toDate();
}

function formatPostTitle(post) {
  let title = post.sourceCreatorHandle;

  if (post.source === SOCIAL_LISTENING_CHANNEL_OPTIONS.REDDIT.value) {
    // Display the subreddit name as the title
    const match = post.permalink?.match(/reddit\.com\/(r\/[^/]+)/);
    if (match?.[1]) {
      title = match[1];
    }
  } else if (post.source === SOCIAL_LISTENING_CHANNEL_OPTIONS.WEB.value) {
    if (post.permalink) {
      const url = new URL(post.permalink);
      title = url.hostname.replace(/^www\./, ''); // Remove 'www.' prefix if present
    }
  }

  return title;
}

function formatPostAvatar(post) {
  let avatar = post.sourceCreatorAvatar;

  if ([CHANNELS.REDDIT.value, CHANNELS.WEB.value].includes(post.source)) {
    // We don't want to display the avatar for these platforms
    avatar = null;
  } else if (avatar === null) {
    // Default avatar in case of missing
    avatar = { image: 'user' };
  }

  return avatar;
}

/**
 * Maps Facebook platform types to dashing-ui media types.
 * Some Facebook platform types don't have a mapping in dashing-ui,
 * and @dashing-wardens asked to handle them as a special case.
 */
function formatPlatformMediaType(post) {
  const mediaTypeMap = {
    photo: 'image',
    album: 'carousel',
    music: 'audio',
    status: 'text',
    event: 'text', // we don't have an event type, using text as a placeholder
  };

  return mediaTypeMap[post.postType] ?? post.postType ?? post.mediaType;
}

export function formatPostDataToMediaCardProps(post) {
  const metricItems = [
    {
      label: 'Mention Type',
      value: mentionTypeMap[post.mentionType] ?? 'Caption',
    },
    {
      label: 'Engagements',
      type: 'Total',
      formatType: 'number',
      value: post.engagements,
    },
    {
      label: 'Sentiment',
      chip: formatSentimentChipProps(post.sentiment),
    },
  ];

  const duration = post.duration ?? post.videoDuration;
  const platformMediaType = formatPlatformMediaType(post);
  const title = formatPostTitle(post);
  const avatar = formatPostAvatar(post);

  return {
    src: post.urls?.thumbs,
    sourceType: post.mediaType.toLowerCase(),
    platformMediaType,
    videoDuration:
      post.mediaType === SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.VIDEO.value
        ? formatVideoDurationInSeconds(duration, post.source)
        : undefined,
    videoPreload: 'none',
    videoPoster: post.urls?.thumbs,
    avatar,
    title,
    postTitle: post.title,
    caption: post.caption,
    channel: post.source.toLowerCase(),
    metricItems,
    text:
      post.mediaType === SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.TEXT.value ||
      post.mediaType === SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.LINK.value
        ? post.caption
        : undefined,
    postDate: formatTopPostPostDate(post.sourceCreatedAt),
    sourceId: post.sourceId,
    source: post.source,
    sizes: post.urls?.imageSizes ?? post.urls?.videoSizes,
  };
}

export function formatPostDataToLibraryCardProps(post) {
  const duration = post.duration ?? post.videoDuration;
  return {
    channel: post.source,
    sourceType: post.mediaType.toLowerCase(),
    platformMediaType: post.mediaType,
    src: post.urls?.thumbs,
    videoDuration:
      post.mediaType === SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.VIDEO.value
        ? formatVideoDurationInSeconds(duration, post.source)
        : undefined,
    videoPreload: 'none',
    videoPoster: post.urls?.thumbs,
    sourceId: post.sourceId,
    source: post.source,
    sizes: post.urls?.imageSizes ?? post.urls?.videoSizes,
  };
}

export function formatMediaToLibraryCardProps(media) {
  const { channelName, mediaType, platformMediaType, mediaSrc, mediaDuration } =
    useMediaData(media);
  return {
    channel: channelName.value,
    sourceType: mediaType.value,
    platformMediaType: platformMediaType.value,
    src: mediaSrc.value,
    videoDuration: mediaDuration.value,
    videoPreload: 'none',
    videoPoster: mediaSrc.value,
    sourceId: media.sourceId,
  };
}

/**
 * Formats post data for DataTable display in list view.
 * This formatter prepares all necessary fields for the PostsDataTable component.
 */
export function formatPostDataToDataTableProps(post) {
  const title = formatPostTitle(post);
  const avatar = formatPostAvatar(post);
  const formattedDate = formatTopPostPostDate(post.sourceCreatedAt, true);
  const mentionType = mentionTypeMap[post.mentionType] ?? 'Caption';
  const sentimentChip = formatSentimentChipProps(post.sentiment);
  const channelName = SOCIAL_LISTENING_CHANNEL_OPTIONS[post.source]?.title;
  const platformMediaType = formatPlatformMediaType(post);

  // Text field for text/link media types
  const text =
    post.mediaType === SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.TEXT.value ||
    post.mediaType === SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.LINK.value
      ? post.caption
      : undefined;

  // Caption text for display and tooltip (fallback order: caption -> title -> text)
  const displayCaption = post.caption || post.title || text || '';

  return {
    src: post.urls?.thumbs,
    videoPreload: 'none',
    videoPoster: post.urls?.thumbs,
    title,
    avatar,
    postDate: formattedDate,
    caption: post.caption,
    postTitle: post.title,
    text,
    displayCaption,
    mentionType,
    channel: post.source.toLowerCase(),
    channelName,
    sentimentChip,
    sourceId: post.sourceId,
    source: post.source,
    platformMediaType,
  };
}

export function getFormattedKeywordPostVolumeTimeSeriesChartData(breakdownGroups, chartConfig) {
  const data = [];

  breakdownGroups.totalPosts.forEach((entry) => {
    data.push({
      x: entry.x,
      y: entry.totalPosts,
    });
  });

  const label = 'totalPosts';
  const upperLabel = label.toUpperCase();
  const baseColor = chartConfig.colour[upperLabel];

  return {
    meters: [
      {
        label: 'Mentions',
        data,
        borderColor: baseColor,
      },
    ],
  };
}

export function getFormattedMultiMetersPostVolumeTimeSeriesChartData(breakdownDataset) {
  const meters = [];

  breakdownDataset.forEach((breakdownGroups) => {
    const data = [];

    breakdownGroups.totalPosts.forEach((entry) => {
      data.push({
        x: entry.x,
        y: entry.totalPosts,
      });
    });

    const chartConfig = {
      label: breakdownGroups.label,
    };

    meters.push({ ...chartConfig, data });
  });

  return meters.sort((a, b) => a.label.localeCompare(b.label));
}

export function getCommentSourceLink(comment, post) {
  // Return existing permalink if available
  if (comment?.permalink) {
    return comment.permalink;
  }

  // Fallback: Try to construct link based on source type
  const source = comment?.source || post?.source;

  if (!source) {
    return undefined;
  }

  // Instagram: Comment permalinks are typically not directly accessible
  // but we can link to the post
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.INSTAGRAM.value) {
    if (post?.permalink) {
      return post.permalink;
    }
    // Instagram format: https://www.instagram.com/p/{shortcode}/
    if (post?.sourceId) {
      return `https://www.instagram.com/p/${post.sourceId}/`;
    }
    return undefined;
  }

  // Twitter/X: Link to specific tweet/reply
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.TWITTER.value) {
    if (comment?.sourceId && comment?.sourceCreatorHandle) {
      // Twitter format: https://twitter.com/{username}/status/{tweetId}
      return `https://twitter.com/${comment.sourceCreatorHandle}/status/${comment.sourceId}`;
    }
    return undefined;
  }

  // Facebook: https://facebook.com/{commentId}
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.FACEBOOK.value) {
    if (comment?.sourceId) {
      return `https://facebook.com/${comment.sourceId}`;
    }
    return undefined;
  }

  // YouTube: Link to comment on video
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.YOUTUBE.value) {
    const postLink = post?.permalink;
    if (postLink && comment?.sourceId) {
      // YouTube format: {videoLink}&lc={commentId}
      return `${postLink}&lc=${comment.sourceId}`;
    }
    return undefined;
  }

  // TikTok: https://www.tiktok.com/business-suite/comments?comment_id={commentId}&video_id={videoId}
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.TIKTOK.value) {
    const videoId = post?.sourceId || comment?.videoId;
    if (comment?.sourceId && videoId) {
      return `https://www.tiktok.com/business-suite/comments?comment_id=${comment.sourceId}&video_id=${videoId}`;
    }
    return undefined;
  }

  // Reddit: Link to comment thread
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.REDDIT.value) {
    if (post?.permalink) {
      return post.permalink;
    }
    return undefined;
  }

  // LinkedIn: Link to post (comment permalinks not directly accessible)
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.LINKEDIN.value) {
    if (post?.permalink) {
      return post.permalink;
    }
    return undefined;
  }

  // Web: Return post permalink
  if (source === SOCIAL_LISTENING_CHANNEL_OPTIONS.WEB.value) {
    return post?.permalink;
  }

  return undefined;
}
